import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Loader2,
  Plus,
  Trash2,
  ShieldCheck,
  RefreshCw,
  Copy,
  Check,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api from "../services/api";

const emptyForm = { name: "", email: "", password: "" };

// A strong random password: 16 chars, guaranteed upper/lower/digit/symbol.
const generatePassword = () => {
  const sets = [
    "ABCDEFGHJKLMNPQRSTUVWXYZ",
    "abcdefghijkmnopqrstuvwxyz",
    "23456789",
    "!@#$%&*?",
  ];
  const all = sets.join("");
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  // One from each set, then fill to length 16, then shuffle.
  const chars = sets.map(pick);
  while (chars.length < 16) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [copied, setCopied] = useState(false);

  const copyPassword = async () => {
    if (!form.password) return;
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  let me = {};
  try {
    me = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    me = {};
  }
  const isSuperAdmin = me.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try {
      // Dedicated endpoint — the paginated /users filter rejects a `role` param.
      const res = await api.get("/v1/admin/admins");
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setAdmins(list);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createAdmin = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error("Name, email and password are required");
      return;
    }
    if (form.password.trim().length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/v1/admin/admins", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      toast.success("Admin created");
      setForm(emptyForm);
      setShowCreate(false);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not create admin");
    } finally {
      setSaving(false);
    }
  };

  const deleteAdmin = async (admin) => {
    const id = String(admin._id || admin.id);
    if (id === String(me._id || me.id)) {
      toast.error("You can't delete your own account");
      return;
    }
    if (!window.confirm(`Delete admin ${admin.name || admin.email}?`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/v1/admin/admins/${id}`);
      toast.success("Admin deleted");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not delete admin");
    } finally {
      setDeletingId(null);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admins</h1>
              <p className="text-gray-500">
                View, create and remove admin accounts.
                {!isSuperAdmin && " (Super-admin only.)"}
              </p>
            </div>
            {isSuperAdmin && (
              <button
                onClick={() => setShowCreate((s) => !s)}
                className="inline-flex items-center gap-2 bg-primary text-black px-4 py-2 rounded-lg font-semibold"
              >
                <Plus size={18} /> New admin
              </button>
            )}
          </div>

          {isSuperAdmin && showCreate && (
            <div className="bg-white border rounded-xl p-5 mb-6 max-w-2xl">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="text-gray-500">Full name <span className="text-red-500">*</span></span>
                  <input
                    value={form.name}
                    onChange={set("name")}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    placeholder="Jane Doe"
                  />
                </label>
                <label className="text-sm">
                  <span className="text-gray-500">Email <span className="text-red-500">*</span></span>
                  <input
                    value={form.email}
                    onChange={set("email")}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    placeholder="jane@zooma.app"
                  />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-gray-500">
                    Password <span className="text-red-500">*</span>
                  </span>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="text"
                      value={form.password}
                      onChange={set("password")}
                      className="flex-1 border rounded-lg px-3 py-2 font-mono"
                      placeholder="Generate or type a password"
                    />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, password: generatePassword() }))}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                      title="Generate a strong password"
                    >
                      <RefreshCw size={15} /> Generate
                    </button>
                    <button
                      type="button"
                      onClick={copyPassword}
                      disabled={!form.password}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap"
                      title="Copy password"
                    >
                      {copied ? <Check size={15} /> : <Copy size={15} />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <span className="text-xs text-gray-400">
                    Share this with the new admin — they sign in with their email
                    and this password.
                  </span>
                </label>
              </div>
              <div className="flex gap-2 justify-end mt-4">
                <button
                  onClick={() => {
                    setShowCreate(false);
                    setForm(emptyForm);
                  }}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </button>
                <button
                  onClick={createAdmin}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold disabled:opacity-60"
                >
                  {saving ? "Creating…" : "Create admin"}
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="animate-spin" size={18} /> Loading admins…
            </div>
          ) : (
            <div className="bg-white border rounded-xl divide-y">
              {admins.length === 0 && (
                <p className="p-5 text-gray-500">No admins found.</p>
              )}
              {admins.map((a) => {
                const id = String(a._id || a.id);
                const isSuper = a.role === "super_admin";
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between gap-4 p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <ShieldCheck
                          size={18}
                          className={isSuper ? "text-primary" : "text-gray-400"}
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {a.name || "—"}{" "}
                          <span
                            className={`ml-1 text-xs px-2 py-0.5 rounded-full ${
                              isSuper
                                ? "bg-amber-100 text-amber-800"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {isSuper ? "Super Admin" : "Admin"}
                          </span>
                        </p>
                        <p className="text-sm text-gray-500">{a.email}</p>
                      </div>
                    </div>
                    {isSuperAdmin && id !== String(me._id || me.id) && (
                      <button
                        onClick={() => deleteAdmin(a)}
                        disabled={deletingId === id}
                        className="inline-flex items-center gap-1 text-red-600 text-sm hover:underline disabled:opacity-50"
                      >
                        <Trash2 size={16} />
                        {deletingId === id ? "Deleting…" : "Delete"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminManagement;
